import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Instagram, BadgeDollarSign } from "lucide-react";
import { STORIES, BONUS_TAG } from "../data/stories";
import { trackEvent, trackStoryClick } from "../lib/analytics";

const ACCENTS = {
    orange: { text: "text-fxtm-orange", border: "hover:border-fxtm-orange/60", glow: "hover:shadow-[inset_0_0_80px_rgba(255,85,0,0.15)]" },
    slate: { text: "text-slate-200", border: "hover:border-slate-300/50", glow: "hover:shadow-[inset_0_0_80px_rgba(148,163,184,0.12)]" },
    green: { text: "text-fxtm-green", border: "hover:border-fxtm-green/60", glow: "hover:shadow-[inset_0_0_80px_rgba(16,185,129,0.15)]" },
    red: { text: "text-fxtm-red", border: "hover:border-fxtm-red/60", glow: "hover:shadow-[inset_0_0_80px_rgba(225,29,72,0.15)]" },
};

// Different hook placement per block so each square feels distinct
const HOOK_PLACEMENT = [
    "justify-end", // 1: hook at bottom
    "justify-between", // 2: hook at top, cta bottom
    "justify-end", // 3: hook at bottom
    "justify-between", // 4: hook at top, cta bottom
];

const Block = ({ story, index }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px" });
    const accent = ACCENTS[story.accent] || ACCENTS.orange;
    const hookTop = HOOK_PLACEMENT[index] === "justify-between";

    useEffect(() => {
        if (inView) {
            trackEvent("story_card_impression", {
                story_id: story.id,
                story_name: story.category,
                content_category: story.category,
                content_type: story.contentType,
            });
        }
    }, [inView, story]);

    const handleClick = () => {
        trackStoryClick(story);
    };

    const hookBlock = (
        <div className={hookTop ? "" : "mt-auto"}>
            <p className={`font-mono text-[10px] sm:text-[11px] tracking-[0.25em] ${accent.text} mb-2.5`} data-testid={`block-hook-label-${story.slug}`}>
                {story.visualHook}
            </p>
            <h2 className="font-syne font-extrabold tracking-tight leading-[1.08] text-white text-[22px] sm:text-2xl lg:text-[28px] xl:text-4xl max-w-md" data-testid={`block-hook-${story.slug}`}>
                {story.hook}
            </h2>
            <p className="text-slate-300/90 text-xs sm:text-sm leading-relaxed mt-3 max-w-sm">
                {story.sub}
            </p>
        </div>
    );

    const ctaBlock = (
        <div className={hookTop ? "mt-auto" : "mt-4"}>
            <div className="mb-3.5" data-testid={`block-bonus-${story.slug}`}>
                <span className="inline-block font-mono text-[8px] sm:text-[9px] font-bold tracking-[0.25em] text-white bg-fxtm-red rounded px-2 py-1 mb-2" data-testid={`block-lto-${story.slug}`}>
                    LIMITED TIME OFFER
                </span>
                <div className="animate-blink inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 shadow-[0_8px_28px_rgba(255,255,255,0.35)] max-w-full">
                    <BadgeDollarSign className="w-4 h-4 text-black shrink-0" />
                    <span className="font-syne font-extrabold text-black text-[10px] sm:text-xs lg:text-sm tracking-wide leading-tight">
                        {BONUS_TAG}
                    </span>
                </div>
            </div>
            <div className="flex items-center justify-between gap-3">
                <span className="font-syne font-bold text-sm sm:text-base text-white tracking-wide" data-testid={`block-cta-${story.slug}`}>
                    {story.cta}
                </span>
                <span className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 shrink-0 rounded-full bg-white text-[#070A10] transition-all duration-300 group-hover:bg-fxtm-orange group-hover:text-white">
                    <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
            </div>
        </div>
    );

    return (
        <motion.a
            ref={ref}
            href={story.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 + index * 0.12 }}
            data-testid={`story-block-${story.slug}`}
            aria-label={`${story.category}: ${story.hook} — continue on Instagram. Includes ${BONUS_TAG.toLowerCase()} information.`}
            className={`group relative block overflow-hidden rounded-2xl min-h-[430px] sm:min-h-[470px] lg:min-h-0 lg:aspect-square border border-[#0B1220]/10 shadow-[0_18px_50px_-20px_rgba(11,18,32,0.35)] transition-all duration-500 ${accent.border} ${accent.glow}`}
        >
            <img
                src={story.image}
                alt={story.altText}
                loading={index < 2 ? "eager" : "lazy"}
                fetchpriority={index < 2 ? "high" : "auto"}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A10]/95 via-[#070A10]/45 to-[#070A10]/35 transition-opacity duration-500 group-hover:via-[#070A10]/35" />

            <div className={`relative h-full flex flex-col ${HOOK_PLACEMENT[index]} p-5 sm:p-6 xl:p-8`}>
                <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-white/70" data-testid={`block-index-${story.slug}`}>
                        0{story.id} — {story.category}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[9px] sm:text-[10px] tracking-[0.18em] text-white/90 bg-black/45 backdrop-blur-md border border-white/15 rounded-full px-3 py-1.5" data-testid={`block-type-${story.slug}`}>
                        <Instagram className="w-3.5 h-3.5" />
                        {story.typeLabel}
                    </span>
                </div>
                {hookTop ? (
                    <>
                        <div className="mt-4">{hookBlock}</div>
                        {ctaBlock}
                    </>
                ) : (
                    <>
                        {hookBlock}
                        {ctaBlock}
                    </>
                )}
            </div>
        </motion.a>
    );
};

export const SquareBlocks = () => (
    <section
        className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-7 lg:gap-9 p-5 sm:p-8 lg:p-10 w-full max-w-6xl mx-auto"        aria-label="Four FXTM trading lessons on Instagram"
        data-testid="story-blocks-grid"
    >
        {STORIES.map((story, i) => (
            <Block key={story.id} story={story} index={i} />
        ))}
    </section>
);
